import { useMemo, useState } from "react";
import { Home, Search } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatDate, formatNumber } from "@/utils/format";
import type { CasaVendidaRow } from "./chartUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  rows: CasaVendidaRow[];
}

export function CasasVendidasModal({ open, onClose, rows }: Props) {
  const [busca, setBusca] = useState("");

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.unidade, r.cliente, r.obra].some((s) => s.toLowerCase().includes(q))
    );
  }, [rows, busca]);

  const valorTotal = useMemo(
    () => filtered.reduce((s, r) => s + r.valor, 0),
    [filtered]
  );
  const precoMedio = filtered.length > 0 ? valorTotal / filtered.length : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <Home className="h-4 w-4" />
          </div>
          <span className="text-sm font-semibold text-slate-800">
            Casas vendidas no período
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Resumo */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-indigo-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Casas</p>
            <p className="mt-0.5 text-lg font-bold text-slate-800">{formatNumber(filtered.length)}</p>
          </div>
          <div className="rounded-xl bg-purple-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">Valor total</p>
            <p className="mt-0.5 text-lg font-bold text-slate-800">{formatCurrency(valorTotal)}</p>
          </div>
          <div className="rounded-xl bg-emerald-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Preço médio</p>
            <p className="mt-0.5 text-lg font-bold text-slate-800">{formatCurrency(precoMedio)}</p>
          </div>
        </div>

        {/* Busca */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por unidade, cliente ou obra…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-violet-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-100"
          />
        </div>

        {/* Tabela */}
        <div className="max-h-[55vh] overflow-auto rounded-xl border border-slate-100">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-100">
                <th className="px-3 py-2 text-left font-semibold text-slate-500">Unidade</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-500">Cliente</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-500">Obra</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-500">Data da venda</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-500">Valor</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.key} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="max-w-[220px] truncate px-3 py-2 font-medium text-slate-700" title={r.unidade}>
                    {r.unidade}
                  </td>
                  <td className="max-w-[220px] truncate px-3 py-2 text-slate-500" title={r.cliente}>
                    {r.cliente}
                  </td>
                  <td className="max-w-[180px] truncate px-3 py-2 text-slate-500" title={r.obra}>
                    {r.obra}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                    {r.data ? formatDate(r.data) : "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                    {formatCurrency(r.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-400">
              {rows.length === 0 ? "Nenhuma casa vendida no período." : "Nenhum resultado para a busca."}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
